import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../types';
import { Button, Input } from '../components';
import { authService } from '../services/auth';
import { supabase } from '../config/supabase';
import { Colors, Fonts, Typography } from '../constants';
import { useResponsive } from '../utils/responsive';
import { useDialog } from '../contexts';

type Props = NativeStackScreenProps<RootStackParamList, 'ResetPassword'>;

export const ResetPasswordScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const dialog = useDialog();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    // Listen for PASSWORD_RECOVERY event from Supabase
    // This fires when the user arrives via the reset password deep link
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event) => {
        if (event === 'PASSWORD_RECOVERY') {
          setSessionReady(true);
        }
      }
    );

    // Also check if we already have a session (e.g., page loaded with token)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setSessionReady(true);
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const handleNewPasswordChange = (text: string) => {
    setNewPassword(text);
    if (newPasswordError) setNewPasswordError('');
  };

  const handleConfirmPasswordChange = (text: string) => {
    setConfirmPassword(text);
    if (confirmPasswordError) setConfirmPasswordError('');
  };

  const handleNewPasswordBlur = () => {
    if (!newPassword) {
      setNewPasswordError(t('common.validation.passwordRequired'));
    } else if (newPassword.length < 6) {
      setNewPasswordError(t('common.validation.passwordMinLength'));
    }
  };

  const handleConfirmPasswordBlur = () => {
    if (!confirmPassword) {
      setConfirmPasswordError(t('common.validation.passwordRequired'));
    } else if (confirmPassword !== newPassword) {
      setConfirmPasswordError(t('common.validation.passwordMismatch'));
    }
  };

  const handleSubmit = async () => {
    setNewPasswordError('');
    setConfirmPasswordError('');

    let valid = true;
    if (!newPassword) {
      setNewPasswordError(t('common.validation.passwordRequired'));
      valid = false;
    } else if (newPassword.length < 6) {
      setNewPasswordError(t('common.validation.passwordMinLength'));
      valid = false;
    }
    if (!confirmPassword) {
      setConfirmPasswordError(t('common.validation.passwordRequired'));
      valid = false;
    } else if (confirmPassword !== newPassword) {
      setConfirmPasswordError(t('common.validation.passwordMismatch'));
      valid = false;
    }
    if (!valid) return;

    if (!sessionReady) {
      dialog.alert(
        t('screens.resetPassword.failed'),
        t('screens.resetPassword.invalidSession'),
        'error'
      );
      return;
    }

    setLoading(true);
    try {
      await authService.updatePassword(newPassword);
      dialog.alert(
        t('screens.resetPassword.successTitle'),
        t('screens.resetPassword.successMessage'),
        'success'
      );
      // User is now authenticated — auth state listener will navigate to Home
    } catch (error: any) {
      dialog.alert(
        t('screens.resetPassword.failed'),
        error.message || t('common.errors.unknown'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.formWrapper}>
          <View style={styles.header}>
            <Text style={[styles.title, !isDesktop && styles.titleMobile]}>
              {t('screens.resetPassword.title')}
            </Text>
            <Text style={styles.subtitle}>{t('screens.resetPassword.subtitle')}</Text>
          </View>

          <View style={styles.form}>
            <Input
              label={t('screens.resetPassword.newPassword')}
              icon="lock-closed-outline"
              value={newPassword}
              onChangeText={handleNewPasswordChange}
              onBlur={handleNewPasswordBlur}
              placeholder={t('screens.resetPassword.newPasswordPlaceholder')}
              secureTextEntry
              autoComplete="new-password"
              returnKeyType="next"
              error={newPasswordError}
            />
            <Input
              label={t('screens.resetPassword.confirmPassword')}
              icon="lock-closed-outline"
              value={confirmPassword}
              onChangeText={handleConfirmPasswordChange}
              onBlur={handleConfirmPasswordBlur}
              placeholder={t('screens.resetPassword.confirmPasswordPlaceholder')}
              secureTextEntry
              autoComplete="new-password"
              returnKeyType="go"
              onSubmitEditing={handleSubmit}
              error={confirmPasswordError}
            />
            <Button
              title={t('screens.resetPassword.submit')}
              onPress={handleSubmit}
              loading={loading}
              style={styles.submitButton}
            />
          </View>

          <Button
            title={t('common.buttons.backToLogin')}
            onPress={() => navigation.navigate('Login')}
            variant="outline"
            style={styles.backButton}
          />
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 24,
    alignItems: 'center',
  },
  formWrapper: {
    width: '100%',
    ...(Platform.OS === 'web' && {
      maxWidth: 500,
    }),
  },
  header: {
    alignItems: 'center',
    marginBottom: 36,
  },
  title: {
    ...Typography.title,
    color: Colors.textDark,
    textAlign: 'center',
    marginBottom: 6,
  },
  titleMobile: {
    ...Typography.displayMobile,
  },
  subtitle: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    lineHeight: 22,
    color: Colors.textMedium,
    textAlign: 'center',
    maxWidth: 380,
  },
  form: {
    width: '100%',
  },
  submitButton: {
    marginTop: 8,
  },
  backButton: {
    marginTop: 16,
  },
});
